import React, { forwardRef, useEffect, useId, useRef, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import {
  MAX_AUTO_ROLL_TENOR_WEEKS,
  MIN_AUTO_ROLL_TENOR_WEEKS,
  STANDARD_TENOR_WEEKS,
  clampTenorWeeks,
  formatTenorWeeks,
} from '@utils/autoRollTenor';
import styles from './AutoRollTenorPicker.module.css';

export interface IAutoRollTenorSuggestion {
  weeks: number;
  /** Short visible tag on the chip, e.g. "This bill". */
  tag: string;
  /** Why this term is suggested; the chip's hover hint and part of its accessible name. */
  reason: string;
}

export interface IAutoRollTenorPickerProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange' | 'defaultValue'
> {
  value: number;
  onChange: (weeks: number) => void;
  label?: string;
  hint?: React.ReactNode;
  disabled?: boolean;
  /** Offered ahead of the standard terms; a standard term with the same weeks is dropped. */
  suggestion?: IAutoRollTenorSuggestion;
  /**
   * Renders the controls as placeholders rather than hiding them, so the choice is visible before
   * it applies. Clicking the picker calls `onActivate`.
   */
  inactive?: boolean;
  inactiveHint?: string;
  onActivate?: () => void;
}

const AutoRollTenorPicker = forwardRef<HTMLDivElement, IAutoRollTenorPickerProps>(
  (
    {
      value,
      onChange,
      label = 'Term',
      hint,
      disabled = false,
      suggestion,
      inactive = false,
      inactiveHint,
      onActivate,
      className,
      ...rest
    },
    ref,
  ) => {
    const inputId = useId();
    const hintId = useId();
    const [draft, setDraft] = useState(String(value));

    useEffect(() => {
      setDraft(String(value));
    }, [value]);

    // The staggered morph runs only on the way out of inactive; afterwards hover feedback is instant.
    const [isWaking, setIsWaking] = useState(false);
    const wasInactiveRef = useRef(inactive);
    useEffect(() => {
      const wasInactive = wasInactiveRef.current;
      wasInactiveRef.current = inactive;
      if (!wasInactive || inactive) return undefined;
      setIsWaking(true);
      const timer = window.setTimeout(() => setIsWaking(false), 700);
      return () => window.clearTimeout(timer);
    }, [inactive]);

    const commit = (weeks: number) => {
      const next = clampTenorWeeks(weeks);
      setDraft(String(next));
      if (next !== value) onChange(next);
    };

    const handleDraftChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value.replace(/\D/g, '');
      setDraft(raw);
      const weeks = Number(raw);
      // Only a complete in-range entry updates the value mid-typing; "1" on the way to "12" must not clamp.
      if (raw !== '' && weeks >= MIN_AUTO_ROLL_TENOR_WEEKS && weeks <= MAX_AUTO_ROLL_TENOR_WEEKS) {
        onChange(weeks);
      }
    };

    const controlsDisabled = disabled || inactive;
    const standardTerms = STANDARD_TENOR_WEEKS.filter((weeks) => weeks !== suggestion?.weeks);

    return (
      <div
        {...rest}
        ref={ref}
        className={`${styles.picker} ${className ?? ''}`}
        data-inactive={inactive || undefined}
        data-waking={isWaking || undefined}
        aria-disabled={inactive || undefined}
      >
        <label htmlFor={inputId} className={styles.label}>
          {label}
        </label>

        <div className={styles.controls}>
          <div className={styles.stepper} data-disabled={(disabled && !inactive) || undefined}>
            <button
              type="button"
              className={styles.stepButton}
              aria-label="One week shorter"
              onClick={() => commit(value - 1)}
              disabled={controlsDisabled || value <= MIN_AUTO_ROLL_TENOR_WEEKS}
            >
              <Minus size={16} strokeWidth={2} aria-hidden="true" />
            </button>
            <span className={styles.valueGroup}>
              <input
                id={inputId}
                className={styles.input}
                type="text"
                inputMode="numeric"
                role="spinbutton"
                aria-valuemin={MIN_AUTO_ROLL_TENOR_WEEKS}
                aria-valuemax={MAX_AUTO_ROLL_TENOR_WEEKS}
                aria-valuenow={value}
                aria-valuetext={formatTenorWeeks(value)}
                aria-describedby={hint ? hintId : undefined}
                value={draft}
                onChange={handleDraftChange}
                onBlur={() => commit(draft === '' ? value : Number(draft))}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    commit(value + 1);
                  } else if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    commit(value - 1);
                  } else if (event.key === 'Enter') {
                    event.preventDefault();
                    commit(draft === '' ? value : Number(draft));
                  }
                }}
                disabled={controlsDisabled}
              />
              <span className={styles.unit} aria-hidden="true">
                {value === 1 ? 'week' : 'weeks'}
              </span>
            </span>
            <button
              type="button"
              className={styles.stepButton}
              aria-label="One week longer"
              onClick={() => commit(value + 1)}
              disabled={controlsDisabled || value >= MAX_AUTO_ROLL_TENOR_WEEKS}
            >
              <Plus size={16} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>

          {(suggestion || standardTerms.length > 0) && (
            <div className={styles.shortcuts} role="group" aria-label="Quick terms">
              {suggestion && (
                <button
                  type="button"
                  className={`${styles.shortcut} ${styles.suggested}`}
                  style={{ '--i': 0 } as React.CSSProperties}
                  aria-pressed={value === suggestion.weeks}
                  aria-label={`${formatTenorWeeks(suggestion.weeks)}, ${suggestion.reason}`}
                  data-tooltip={inactive ? undefined : suggestion.reason}
                  onClick={() => commit(suggestion.weeks)}
                  disabled={controlsDisabled}
                >
                  <span className={styles.suggestedContent}>
                    <span className={styles.suggestedTag}>{suggestion.tag}</span>
                    <span className={styles.suggestedWeeks}>
                      {formatTenorWeeks(suggestion.weeks)}
                    </span>
                  </span>
                </button>
              )}
              {standardTerms.map((weeks, index) => (
                <button
                  key={weeks}
                  type="button"
                  className={styles.shortcut}
                  style={{ '--i': index + (suggestion ? 1 : 0) } as React.CSSProperties}
                  aria-pressed={value === weeks}
                  onClick={() => commit(weeks)}
                  disabled={controlsDisabled}
                >
                  {formatTenorWeeks(weeks)}
                </button>
              ))}
            </div>
          )}
          {inactive && onActivate && (
            // Pointer-only shortcut: keyboard and screen-reader users switch on through the page's own
            // choice control, so this stays out of the tab order and the accessibility tree.
            <button
              type="button"
              className={styles.activator}
              tabIndex={-1}
              aria-hidden="true"
              onClick={onActivate}
              disabled={disabled}
            >
              {inactiveHint && <span className={styles.activatorHint}>{inactiveHint}</span>}
            </button>
          )}
        </div>

        {hint && (
          <p id={hintId} className={styles.hint}>
            {hint}
          </p>
        )}
      </div>
    );
  },
);

AutoRollTenorPicker.displayName = 'AutoRollTenorPicker';

export default AutoRollTenorPicker;
